import type { RoleName } from '@classgo/shared';
import { EmptyState } from '@classgo/ui';
import { Link } from '@tanstack/react-router';
import type { ComponentType, ReactElement } from 'react';

import { useT } from '../i18n';
import { allowed } from '../shell';
import { useTenant } from '../tenant/tenant-provider';

/**
 * The screen a person reaches by URL without the permission it needs: no requests are fired, no
 * error toasts, one sentence and the way back. The API refuses regardless; this keeps it clean.
 */
const NoAccessScreen = () => {
  const t = useT();
  return (
    <EmptyState
      title={t('access.denied.title')}
      description={t('access.denied.body')}
      action={
        <Link
          to="/"
          className="text-sm font-medium text-brand-700 underline underline-offset-2 hover:decoration-2"
        >
          {t('access.denied.home')}
        </Link>
      }
    />
  );
};

/**
 * Wraps a screen so it renders only for a session holding `permission`, or one of the `roles`
 * the screen serves without a key (a teacher on Albert, ADR-0012); anyone else sees the
 * no-access screen. The session is already loaded by the app route, so this never flickers.
 * @param {ComponentType} Screen -- The screen component.
 * @param {string} permission -- The permission the screen needs.
 * @param {readonly RoleName[]} [roles] -- Roles admitted without the key, as the API's `roleScoped`.
 * @returns {() => ReactElement} The guarded screen, a plain function component the router accepts.
 */
export const guarded = (
  Screen: ComponentType,
  permission: string,
  roles: readonly RoleName[] = [],
): (() => ReactElement) => {
  const Guarded = () => {
    const { session } = useTenant();
    return allowed(session, permission, roles) ? <Screen /> : <NoAccessScreen />;
  };
  Guarded.displayName = `Guarded(${Screen.displayName ?? Screen.name})`;
  return Guarded;
};
